/**
 * Auto-resize any client-side image whose longest edge exceeds MAX_DIM before
 * upload, so the backend / vision model never sees an oversized frame.
 *
 * Behaviour:
 *  - PDFs and non-image files pass through untouched.
 *  - Images ≤ MAX_DIM on both edges pass through untouched.
 *  - Images with longest edge > MAX_DIM are downscaled proportionally, encoded
 *    as JPEG (0.85 quality), and returned as a new File with a .jpg suffix.
 *  - HEIC/HEIF cannot be decoded by <img> in every browser — if decode fails,
 *    we silently fall back to the original file rather than blocking the user.
 *
 * Users should never see a "too large" error — this is a silent guardrail.
 */

const MAX_DIM = 2000;
const OUTPUT_QUALITY = 0.85;

/**
 * @param {File} file
 * @returns {Promise<File>} the same file (unchanged) or a downscaled JPEG File.
 */
export async function resizeImageIfNeeded(file) {
  if (!file || !(file instanceof File) || !file.type?.startsWith("image/")) return file;

  // Skip GIFs (animation would be lost) and SVG (vector).
  if (file.type === "image/gif" || file.type === "image/svg+xml") return file;

  const url = URL.createObjectURL(file);
  try {
    const bitmap = await loadImage(url);
    const { width, height } = bitmap;
    const longest = Math.max(width, height);
    if (longest <= MAX_DIM) return file;

    const scale = MAX_DIM / longest;
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    // High-quality downscale
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", OUTPUT_QUALITY),
    );
    if (!blob) return file;

    const baseName = (file.name || "upload").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    // Any decode failure (HEIC in Firefox, corrupt file, etc.) — fall back
    // to the original file so upload still proceeds.
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
